import type { ConnectorChannel } from "#features/conversations/types/channels.tsx";
import type { AssistantStatus } from "#features/conversations/utils/assistantStatus.ts";
import type { ConnectedIntegration } from "#types/types.ts";

/**
 * Thinking step codes that map to a subset of AssistantStatus values.
 *
 * @see AssistantStatus in #features/conversations/utils/assistantStatus.ts
 */
export const BackendThinkingStepCodes = {
    thinking: "thinking",
    searching: "searching",
    toolRunning: "tool_running",
    processingPageAction: "processing_page_action",
    assistantMessage: "assistant_message",
    compacting: "compacting"
} as const satisfies Record<string, AssistantStatus>;

/**
 * Frontend-only thinking step codes that don't exist in AssistantStatus.
 * These are UI-specific concepts for displaying additional information
 * to the user during the conversation flow.
 */
export const FrontendThinkingStepCodes = {
    sources: "sources",
    skillLoaded: "skill_loaded"
} as const;

/**
 * All thinking step codes (backend + frontend).
 * Use these constants throughout the application instead of hardcoded strings.
 */
export const ThinkingStepCodes = {
    ...BackendThinkingStepCodes,
    ...FrontendThinkingStepCodes
} as const;

/**
 * Type representing all valid thinking step codes.
 * This is a union of all possible code values.
 */
export type ThinkingStepCode = typeof ThinkingStepCodes[keyof typeof ThinkingStepCodes];

/**
 * Thinking step state constants.
 * A step can be either active (currently in progress) or completed.
 */
export const ThinkingStepStates = {
    active: "active",
    completed: "completed"
} as const;

/**
 * Type representing thinking step states.
 */
export type ThinkingStepState = typeof ThinkingStepStates[keyof typeof ThinkingStepStates];

/**
 * Represents a single step in the assistant's thinking process.
 * Each step has a code (what type of step), a state (active or completed),
 * and optional metadata like details, source count, and source titles.
 */
export interface AssistantThinkingStep {
    id: string;
    code: ThinkingStepCode;
    state: ThinkingStepState;
    title?: string;
    detail?: string;
    iconKey?: string;
    connectedIntegration?: ConnectedIntegration;
    sourceCount?: number;
    sourceTitles?: string[];
    sourceChannels?: ConnectorChannel[];
}

/**
 * Set of AssistantStatus values that should be displayed as thinking steps.
 * Statuses not in this set (e.g. "idle", "error", "blocked") are silently ignored.
 *
 * To surface a new backend status as a thinking step, add it to BackendThinkingStepCodes
 * above AND update the following switches to handle the new code:
 *   - `getDefaultStepTitle` in useThinkingStepDefaults.ts — fallback label shown in the header and step row
 *   - `getDefaultStepDetail` in useThinkingStepDefaults.ts — fallback detail shown when expanded
 *   - `stepIcon` in ThinkingStepItem.tsx — icon rendered next to the step label
 *
 * If the backend always sends `title`, `detail`, and `iconKey` for the new status,
 * the switches become optional (the UI will fall back to undefined/no-icon), but
 * updating them is still recommended for a consistent experience.
 */
export const ThinkingStepStatuses = new Set<AssistantStatus>(Object.values(BackendThinkingStepCodes));
